import React, { useMemo, useContext } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'

import { getTickValues } from '@/components/charts/helpers'
import LineChart from '@/components/charts/LineChart'
import WidgetContext from '@/components/widget/widget.context'
import WidgetContainer from '@/components/widget/WidgetContainer'
import { CHART } from '@/components/charts/chartPalette'
import { flattenTableData } from '@/views/RealtimeCoaching/Reports/ReportsHelpers'

export const ReportsAlertTrendsChart = () => {
  const { data } = useSelector((state) => state.realtimeReports)
  const { hiddenItems } = useContext(WidgetContext)

  const alertTypeColors = {
    negative: CHART.CRITICAL,
    positive: CHART.SUCCESS,
    informative: CHART.INFORMATIVE,
  }
  const alertTypeOrder = ['negative', 'positive', 'informative']
  const sortedColors = alertTypeOrder.map((alertType) => alertTypeColors[alertType])
  const sortedData = alertTypeOrder.map((alertType) => data.alertCountsByTypeByDay[alertType])
  const graphData = sortedData.map((listOfAlertsForAlertType, index) => {
    const alertType = alertTypeOrder[index]
    const formattedAlertData = listOfAlertsForAlertType.map((datum) => {
      // Remove year & hyphen from [YYYY-MM-DD]
      const dateStringWithoutYear = moment(datum.day, 'YYYY-MM-DD').format('MM-DD')

      return { x: dateStringWithoutYear, y: datum.count }
    })

    return {
      id: alertType,
      color: sortedColors[index],
      data: formattedAlertData, // {x: value, y: value}
    }
  })

  const dataWithHiddenItems = useMemo(() => {
    return graphData.filter((item) => !hiddenItems.includes(item.id))
  }, [graphData, hiddenItems])
  const colors = useMemo(() => dataWithHiddenItems.map((data) => data.color), [dataWithHiddenItems])
  const checkIfGraphEmpty = () => {
    const { positive, negative, informative } = data.alertCountsByTypeByDay
    const totalAlertCount = positive.length + negative.length + informative.length

    return totalAlertCount <= 0
  }
  const tableData = useMemo(() => {
    return flattenTableData(dataWithHiddenItems)
  }, [dataWithHiddenItems])
  return (
    <WidgetContainer
      noData={checkIfGraphEmpty()}
      withLegend
      capitalizeLegend
      dataForLegend={{ data: graphData }}
      legendAccessor="id"
      chart={
        <LineChart
          data={dataWithHiddenItems}
          colors={colors}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Date [Day]',
            legendOffset: 36,
            legendPosition: 'middle',
            tickValues: getTickValues(tableData, 'x'),
          }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Alert Count',
            legendOffset: -60,
            legendPosition: 'middle',
          }}
          trendlines={alertTypeOrder
            .filter((alertType) => !hiddenItems.includes(alertType))
            .map((alertType) => ({ serieId: alertType, color: alertTypeColors[alertType] }))}
        />
      }
    />
  )
}
